import {
  MerchantCashAdvance,
  MerchantCashAdvanceDocument,
  MerchantCashAdvanceStatus,
  MerchantCashAdvanceStatusHistoryDocument,
  useChangeMerchantCashAdvanceStatusMutation
} from '../../graphql/types';
import IconButton from '../../components/@extended/IconButton';
import { EditFilled } from '@ant-design/icons';
import { Box, ClickAwayListener, List, Paper, Popper, useMediaQuery, Divider, Badge, BadgeProps, Chip } from '@mui/material';
import React, { useRef, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Transitions from '../../components/@extended/Transitions';
import { dispatch } from '../../store';
import { openSnackbar } from '../../store/reducers/snackbar';
import { BootstrapTooltip, getStatusLabel, getStatusTooltipTitle } from './McaStatusChip';

export const statusColorMap = Object.freeze<Record<MerchantCashAdvanceStatus, string>>({
  [MerchantCashAdvanceStatus.Open]: '#4169E1',
  [MerchantCashAdvanceStatus.Completed]: '#228B22',
  [MerchantCashAdvanceStatus.InHouseLegal]: '#C71585',
  [MerchantCashAdvanceStatus.Attorney]: '#DB7093',
  [MerchantCashAdvanceStatus.Collections]: '#800000',
  [MerchantCashAdvanceStatus.Slow]: '#00BFFF',
  [MerchantCashAdvanceStatus.WrittenOff]: '#778899',
  [MerchantCashAdvanceStatus.NotPaying]: '#FFA500',
  [MerchantCashAdvanceStatus.PrepLegal]: '#FF6347',
  [MerchantCashAdvanceStatus.Settled]: '#32CD32',
  [MerchantCashAdvanceStatus.VerySlow]: '#00CED1',
  [MerchantCashAdvanceStatus.Resumed]: '#6A5ACD',
  [MerchantCashAdvanceStatus.Paused]: '#B8860B',
  [MerchantCashAdvanceStatus.Deleted]: '#000000'
});

export const statusTextColor = (status: MerchantCashAdvanceStatus): string => statusColorMap[status] ?? '#bfbfbf';

export const McaStatusManagementTypeBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: -10,
    top: 0,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: '0 4px'
  }
}));

export interface Props {
  mca: MerchantCashAdvance;
}
const allowedStatuses = [
  MerchantCashAdvanceStatus.Completed,
  MerchantCashAdvanceStatus.Settled,
  MerchantCashAdvanceStatus.WrittenOff,
  MerchantCashAdvanceStatus.Open,
  MerchantCashAdvanceStatus.Slow,
  MerchantCashAdvanceStatus.VerySlow,
  MerchantCashAdvanceStatus.Resumed,
  MerchantCashAdvanceStatus.Paused,
  MerchantCashAdvanceStatus.NotPaying,
  MerchantCashAdvanceStatus.PrepLegal,
  MerchantCashAdvanceStatus.InHouseLegal,
  MerchantCashAdvanceStatus.Attorney,
  MerchantCashAdvanceStatus.Collections
];

export const ChangeMcaStatus = ({ mca }: Props) => {
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
  const anchorRef = useRef<any>(null);
  const [changeStatus, { loading }] = useChangeMerchantCashAdvanceStatusMutation();

  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleChipClick = async (status: MerchantCashAdvanceStatus) => {
    setOpen(false);
    if (mca.status === status) {
      return;
    }
    await changeStatus({
      variables: {
        mcaId: mca.id,
        status
      },
      refetchQueries: [
        { query: MerchantCashAdvanceDocument, variables: { id: mca?.id } },
        { query: MerchantCashAdvanceStatusHistoryDocument, variables: { id: mca?.id } }
      ]
    });
    dispatch(
      openSnackbar({
        open: true,
        message: 'Status has been changed.',
        variant: 'alert',
        alert: {
          color: 'success'
        },
        close: true
      })
    );
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75, zIndex: 1000, pt: 0.25 }}>
      <IconButton
        color="info"
        size="small"
        sx={{ color: '#bfbfbf' }}
        aria-label="open localization"
        ref={anchorRef}
        aria-controls={open ? 'localization-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        disabled={loading}
      >
        <EditFilled />
      </IconButton>
      <Popper
        placement={'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [matchesXs ? 0 : 0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={matchesXs ? 'top-right' : 'top'} in={open} {...TransitionProps}>
            <Paper sx={{ boxShadow: theme.customShadows.z1 }}>
              <ClickAwayListener onClickAway={handleClose}>
                <List
                  component="nav"
                  sx={{
                    p: 0,
                    width: '100%',
                    minWidth: 150,
                    maxWidth: 200,
                    bgcolor: theme.palette.background.paper,
                    borderRadius: 0.5,
                    [theme.breakpoints.down('md')]: {
                      maxWidth: 250
                    }
                  }}
                >
                  {allowedStatuses.map((key) => (
                    <div key={key}>
                      <BootstrapTooltip title={getStatusTooltipTitle(key)} placement="right">
                        <Chip
                          label={getStatusLabel(key)}
                          sx={{
                            marginBottom: '2px',
                            height: '20px',
                            cursor: 'pointer',
                            backgroundColor: statusTextColor(key)
                          }}
                          onClick={() => handleChipClick(key)}
                        />
                      </BootstrapTooltip>
                      <Divider sx={{ backgroundColor: '#313745' }} />
                    </div>
                  ))}
                </List>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};
